import React, { useEffect } from "react";
import { Info, ArrowUpRight, MessageCircleQuestion } from "lucide-react";
import { BUSINESS } from "@/data/business";
import { getServiceIcon } from "@/lib/serviceIcons";
import { BookButton } from "@/components/site/BookButton";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const slug = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/** Inject FAQPage structured data for richer Google search results. */
const useFaqSchema = () => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: BUSINESS.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    let script = document.getElementById("faq-schema");
    if (!script) {
      script = document.createElement("script");
      script.id = "faq-schema";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    return () => {
      document.getElementById("faq-schema")?.remove();
    };
  }, []);
};

const PriceBadge = ({ price, name }) => (
  <Badge
    data-testid={`service-price-${slug(name)}`}
    variant="secondary"
    className="whitespace-nowrap rounded-full border border-[color:rgba(43,31,23,0.12)] bg-[color:rgba(217,183,122,0.3)] px-3.5 py-1 text-[13px] font-semibold tracking-wide text-[color:var(--text)]"
  >
    From ${price}
  </Badge>
);

const ServiceCard = ({ service }) => (
  <div data-testid={`service-card-${slug(service.name)}`} className="glass glass-hover flex h-full flex-col p-7">
    <div className="flex items-start justify-between gap-3">
      <h3 className="text-xl font-medium leading-snug">{service.name}</h3>
      <PriceBadge price={service.price} name={service.name} />
    </div>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-[color:var(--text-muted)]">{service.desc}</p>
    <a
      href={BUSINESS.bookingUrl}
      target="_blank"
      rel="noreferrer"
      data-testid={`service-book-${slug(service.name)}`}
      className="group/book mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--text)] link-underline"
      aria-label={`Book ${service.name} on our Square booking page`}
    >
      Book this treatment
      <ArrowUpRight
        size={15}
        strokeWidth={1.75}
        className="transition-transform duration-200 group-hover/book:translate-x-0.5 group-hover/book:-translate-y-0.5"
      />
    </a>
  </div>
);

const CategorySection = ({ category, index }) => {
  const CatIcon = getServiceIcon(category.icon);
  return (
    <section
      data-testid={`services-category-${slug(category.title)}`}
      className={`section-pad ${index % 2 === 1 ? "bg-[color:rgba(217,183,122,0.13)]" : ""}`}
    >
      <div className="container-x">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-[14px] bg-[color:rgba(217,183,122,0.34)]">
            <CatIcon size={22} strokeWidth={1.6} className="text-[color:var(--text)]" />
          </span>
          <h2 className="mt-4 text-3xl font-light tracking-tight sm:text-4xl">{category.title}</h2>
          <p className="mt-3 text-base leading-relaxed text-[color:var(--text-muted)]">{category.subtitle}</p>
        </Reveal>
        <div
          className={`mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 ${
            category.items.length === 4 ? "lg:grid-cols-2 lg:gap-7" : "lg:grid-cols-3"
          }`}
        >
          {category.items.map((s, i) => (
            <Reveal key={s.name} delay={Math.min(i, 5) * 0.05}>
              <ServiceCard service={s} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const AdditionalServicesSection = ({ additional }) => (
  <section data-testid="services-additional" className="section-pad pt-0">
    <div className="container-x">
      <Reveal>
        <div className="cta-dark beam-top noise relative overflow-hidden px-6 py-12 sm:px-12 sm:py-16">
          <div className="keyline-glow keyline-glow--contained absolute left-0 right-0 top-0" aria-hidden="true" />
          <div className="blob left-[-8%] top-[-30%] h-[280px] w-[280px] bg-[color:rgba(200,154,74,0.35)]" aria-hidden="true" />
          <div className="relative">
            <div className="mx-auto max-w-2xl text-center">
              <span className="eyebrow-light">{additional.title}</span>
              <p className="mt-4 text-base leading-relaxed text-[color:rgba(247,241,232,0.78)]">
                {additional.subtitle}
              </p>
            </div>
            <div className="mt-9 flex flex-wrap justify-center gap-4 sm:gap-6">
              {additional.items.map((s, i) => {
                const Icon = getServiceIcon(s.icon);
                return (
                  <Reveal key={s.name} delay={Math.min(i, 5) * 0.06} className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]">
                    <div
                      data-testid={`additional-service-${slug(s.name)}`}
                      className="flex h-full flex-col rounded-[18px] border border-[color:rgba(247,241,232,0.16)] bg-[color:rgba(247,241,232,0.07)] p-6 backdrop-blur-md"
                    >
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-[12px] bg-[color:rgba(247,241,232,0.12)]">
                        <Icon size={20} strokeWidth={1.6} className="text-[color:rgba(247,241,232,0.92)]" />
                      </span>
                      <h3 className="mt-4 text-lg font-medium text-[color:var(--on-color)]">{s.name}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[color:rgba(247,241,232,0.72)]">{s.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const FaqSection = () => (
  <section data-testid="services-faq" className="section-pad pt-0">
    <div className="container-x">
      <Reveal className="mx-auto max-w-2xl text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-[14px] bg-[color:rgba(185,163,143,0.22)]">
          <MessageCircleQuestion size={22} strokeWidth={1.6} className="text-[color:var(--text)]" />
        </span>
        <h2 className="mt-4 text-3xl font-light tracking-tight sm:text-4xl">Frequently asked questions</h2>
        <p className="mt-3 text-base leading-relaxed text-[color:var(--text-muted)]">
          Everything you need to know about treatments, preparation, and aftercare, so you can book with confidence.
        </p>
      </Reveal>
      <Reveal delay={0.08} className="mx-auto mt-10 max-w-3xl">
        <div className="glass px-5 py-2 sm:px-8 sm:py-3">
          <Accordion type="single" collapsible className="w-full">
            {BUSINESS.faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className={i === BUSINESS.faqs.length - 1 ? "border-b-0" : "border-[color:rgba(43,31,23,0.08)]"}
              >
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="py-5 text-left text-base font-medium hover:no-underline sm:text-lg"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-answer-${i}`}
                  className="pb-5 text-sm leading-relaxed text-[color:var(--text-muted)] sm:text-[15px]"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Reveal>
    </div>
  </section>
);

const ServicesCta = () => (
  <section className="section-pad pt-0">
    <div className="container-x">
      <Reveal>
        <div className="glass-strong px-6 py-12 text-center sm:px-12 sm:py-16">
          <h2 className="mx-auto max-w-2xl text-3xl font-light tracking-tight sm:text-4xl">
            Not sure which treatment is right for you?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[color:var(--text-muted)]">
            Start with a consultation. We'll analyze your skin and create a personalized plan just for you.
          </p>
          <div className="mt-8 flex justify-center">
            <BookButton testId="services-book-appointment-button" className="px-8 py-3.5" />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Services() {
  const { menu } = BUSINESS;
  useFaqSchema();

  return (
    <div data-testid="services-page">
      <Seo
        title="Services & Pricing | Facials, HydraFacial, Dermaplaning & More | Bahra Skincare"
        description="Explore our full service menu: classic, anti-aging, brightening and acne facials, HydraFacial, dermaplaning, LED light therapy, microcurrent, Derma Pen treatments, chemical exfoliation and more. Prices from $15. Book online in Niles, IL."
        path="/services"
      />
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="hero-accent absolute inset-0" aria-hidden="true" />
        <div className="container-x relative py-14 text-center sm:py-20">
          <Reveal className="mx-auto max-w-2xl">
            <span className="eyebrow">Our Services</span>
            <h1 className="mt-4 text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Treatments tailored to your skin
            </h1>
            <p className="mt-6 text-base leading-relaxed text-[color:var(--text-muted)] sm:text-lg">
              A full menu of facials, advanced treatments, and finishing touches, each crafted around
              your skin with an organic, natural approach.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing note */}
      <section className="container-x -mt-2">
        <Reveal>
          <div data-testid="services-pricing-note" className="glass flex items-start gap-3 p-5 sm:items-center">
            <Info size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-[color:var(--text)] sm:mt-0" />
            <p className="text-sm leading-relaxed text-[color:var(--text-muted)]">{menu.note}</p>
          </div>
        </Reveal>
      </section>

      {/* Menu categories */}
      {menu.categories.map((cat, ci) => (
        <CategorySection key={cat.title} category={cat} index={ci} />
      ))}

      <AdditionalServicesSection additional={menu.additional} />
      <FaqSection />
      <ServicesCta />
    </div>
  );
}
